/**
 * 条件ごとの睡眠の比較（直近 90 日の主睡眠）
 */

import type { SleepPattern } from '@/api/types/sleep'
import { cn } from '@/lib/utils'

import { formatMinutes } from '../format'

type Props = {
  patterns: SleepPattern[]
  /** 今回の睡眠が入る群を強調する */
  highlightCurrent?: boolean
}

const value = (values: Record<string, number>, key: string): number | undefined =>
  new Map(Object.entries(values)).get(key)

export const PatternCard = ({ patterns, highlightCurrent = false }: Props) => {
  if (patterns.length === 0) return null
  return (
    <section className="space-y-3 rounded-xl border bg-card p-4 shadow-sm">
      <div>
        <p className="text-sm font-medium">あなたの睡眠の傾向</p>
        <p className="text-[11px] text-muted-foreground">直近90日の主睡眠を条件で分けた中央値</p>
      </div>
      {patterns.map((p) => {
        const bestScore = Math.max(...p.groups.map((g) => value(g.values, 'score') ?? -1))
        return (
          <div key={p.key}>
            <p className="mb-1 text-xs font-medium text-muted-foreground">{p.label}</p>
            <div className="space-y-1">
              {p.groups.map((g) => {
                const score = value(g.values, 'score')
                const sleepMin = value(g.values, 'sleep_min')
                const deep = value(g.values, 'deep_pct')
                const current = highlightCurrent && g.name === p.current_group
                return (
                  <div
                    key={g.name}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-2 py-1 text-xs',
                      current ? 'bg-indigo-500/10 ring-1 ring-indigo-500/40' : 'bg-muted/40',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {g.name}
                      {current && <span className="ml-1 text-indigo-600 dark:text-indigo-300">← 今回</span>}
                    </span>
                    <span className="w-10 text-right text-muted-foreground tabular-nums">{g.n}回</span>
                    <span
                      className={cn(
                        'w-16 text-right font-semibold tabular-nums',
                        score === bestScore && 'text-emerald-600 dark:text-emerald-400',
                      )}
                    >
                      {score !== undefined ? `スコア${score.toFixed(0)}` : '--'}
                    </span>
                    <span className="w-16 text-right tabular-nums">
                      {sleepMin !== undefined ? formatMinutes(sleepMin) : '--'}
                    </span>
                    <span className="w-14 text-right text-muted-foreground tabular-nums">
                      {deep !== undefined ? `深${deep.toFixed(0)}%` : '--'}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </section>
  )
}
